/**
 * EditScheduleModal - 수업 정보 수정(reschedule) 모달 (instructor-web)
 *
 * admin-web의 동명 모달을 instructor-web 컨벤션(design-system)으로 이식한 것.
 * 강사 전용 `updateInstructorSchedule`을 호출하므로 강사도 수정 가능.
 * 시간/날짜·강사·제목·정원을 수정한다. 예약 회원(bookings)·type은 변경하지 않는다.
 */

import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { Card, Button, Alert } from "@vlab-corporation/design-system";
import type { StudiomateScheduleItem } from "@vlab-corporation/studiomate-api";
import { useUpdateSchedule, type UpdateScheduleParams } from "@/hooks/useCreateSchedule";
import { composeDateTime, shiftEndPreservingDuration } from "@/utils/schedule-datetime";
import { DatePickerField } from "@/components/DatePickerField";
import { TimeSelectField } from "@/components/TimeSelectField";

interface EditScheduleModalProps {
  isOpen: boolean;
  centerId: number;
  schedule: StudiomateScheduleItem;
  onClose: () => void;
  onUpdated: () => void;
}

function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) return msg;
  }
  return null;
}

export function EditScheduleModal({
  isOpen,
  centerId,
  schedule,
  onClose,
  onUpdated,
}: EditScheduleModalProps) {
  const initialStart = composeDateTime(schedule.date, schedule.startTime);
  const initialEnd = composeDateTime(schedule.date, schedule.endTime);

  const [title, setTitle] = useState(schedule.title);
  const [date, setDate] = useState(schedule.date);
  const [startTime, setStartTime] = useState(schedule.startTime);
  const [endTime, setEndTime] = useState(schedule.endTime);
  const [staffName, setStaffName] = useState(schedule.staffName);
  const [maxTraineeInput, setMaxTraineeInput] = useState(String(schedule.maxTrainees));
  const [validationError, setValidationError] = useState<string | null>(null);

  const updateSchedule = useUpdateSchedule();

  useEffect(() => {
    if (isOpen) {
      setTitle(schedule.title);
      setDate(schedule.date);
      setStartTime(schedule.startTime);
      setEndTime(schedule.endTime);
      setStaffName(schedule.staffName);
      setMaxTraineeInput(String(schedule.maxTrainees));
      setValidationError(null);
      updateSchedule.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, schedule]);

  if (!isOpen) return null;

  // 시작 시간을 옮기면 기존 수업 길이를 유지하도록 종료 시간도 같은 간격으로 옮긴다.
  const handleStartTimeChange = (next: string) => {
    setEndTime(shiftEndPreservingDuration(startTime, endTime, next));
    setStartTime(next);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setValidationError("수업명을 입력해주세요.");
      return;
    }
    const trimmedStaff = staffName.trim();
    if (!trimmedStaff) {
      setValidationError("강사명을 입력해주세요.");
      return;
    }
    if (!date || !startTime || !endTime) {
      setValidationError("날짜와 시작/종료 시간을 입력해주세요.");
      return;
    }
    if (endTime <= startTime) {
      setValidationError("종료 시간은 시작 시간보다 뒤여야 합니다.");
      return;
    }
    const maxTrainee = Number(maxTraineeInput);
    if (!Number.isInteger(maxTrainee) || maxTrainee < 1) {
      setValidationError("최대 인원은 1 이상의 정수로 입력해주세요.");
      return;
    }

    const startOn = composeDateTime(date, startTime);
    const endOn = composeDateTime(date, endTime);

    const params: UpdateScheduleParams = { centerId, scheduleId: schedule.id };
    if (trimmedTitle !== schedule.title) params.title = trimmedTitle;
    if (startOn !== initialStart) params.startOn = startOn;
    if (endOn !== initialEnd) params.endOn = endOn;
    if (trimmedStaff !== schedule.staffName) params.staffName = trimmedStaff;
    if (maxTrainee !== schedule.maxTrainees) params.maxTrainee = maxTrainee;

    const changed = Object.keys(params).filter((k) => k !== "centerId" && k !== "scheduleId");
    if (changed.length === 0) {
      setValidationError("변경된 내용이 없습니다.");
      return;
    }

    setValidationError(null);
    updateSchedule.mutate(params, { onSuccess: () => onUpdated() });
  };

  const serverErrorMessage = updateSchedule.isError ? getErrorMessage(updateSchedule.error) : null;

  const inputClass =
    "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-400";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <Card className="w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">수업 수정</h2>
        <p className="text-sm text-gray-500 mb-4">
          유형: {schedule.type === "G" ? "그룹" : "개인"} (변경 불가) · 예약 회원은 유지됩니다.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="editScheduleTitle"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              수업명 <span className="text-red-500">*</span>
            </label>
            <input
              id="editScheduleTitle"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editScheduleDate"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              날짜 <span className="text-red-500">*</span>
            </label>
            <DatePickerField id="editScheduleDate" value={date} onChange={setDate} />
          </div>

          <div className="mb-4 flex gap-3">
            <div className="flex-1">
              <span className="block text-sm font-medium text-gray-700 mb-1">
                시작 <span className="text-red-500">*</span>
              </span>
              <TimeSelectField label="시작" value={startTime} onChange={handleStartTimeChange} />
            </div>
            <div className="flex-1">
              <span className="block text-sm font-medium text-gray-700 mb-1">
                종료 <span className="text-red-500">*</span>
              </span>
              <TimeSelectField label="종료" value={endTime} onChange={setEndTime} />
            </div>
          </div>

          <div className="mb-4">
            <label
              htmlFor="editScheduleStaff"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              강사 <span className="text-red-500">*</span>
            </label>
            <input
              id="editScheduleStaff"
              type="text"
              value={staffName}
              onChange={(e) => setStaffName(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="editScheduleMax"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              최대 인원 <span className="text-red-500">*</span>
            </label>
            <input
              id="editScheduleMax"
              type="number"
              step={1}
              min={1}
              value={maxTraineeInput}
              onChange={(e) => setMaxTraineeInput(e.target.value)}
              className={inputClass}
              required
            />
          </div>

          {validationError && (
            <Alert variant="error" className="mb-4">
              {validationError}
            </Alert>
          )}

          {updateSchedule.isError && !validationError && (
            <Alert variant="error" className="mb-4">
              {serverErrorMessage ?? "수업 수정에 실패했습니다. 다시 시도해주세요."}
            </Alert>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" variant="primary" disabled={updateSchedule.isPending}>
              {updateSchedule.isPending ? "저장 중..." : "저장"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
